import { Formik } from 'formik';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { getAddressForSubmission, getInitialAddressValues } from '../../../utils/address';
import callApi from '../../../utils/callApi';
import { AddressSuggestion, LOADING_TYPES, useCheckoutState } from '../../../utils/checkoutState';
import { config } from '../../../utils/config';
import { pushErrorTrackingEventFromErrorObject } from '../../../utils/tracking';
import Button from '../../atoms/Button';
import AddressForm from '../AddressForm';
import { showDefaultError } from '../Notifications';

export type AddressOverviewFormProps = {
  setEditingAddress: (isEditingAddress: boolean) => void;
  isEditingAddress: boolean;
};

const AddressOverviewForm = ({ setEditingAddress, isEditingAddress }: AddressOverviewFormProps) => {
  const { setShippingAddress, checkoutState, isLoading, setLoading } = useCheckoutState();
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>();
  const { customer, orderState } = checkoutState;
  const hasShippingAddress = !!orderState.addresses!.shipping!;
  const isExpressCheckoutReviewPage = !!checkoutState.isExpressCheckout;

  const availableCountries = config.availableCountries.shipping?.map(({ iso2Code }) => iso2Code) ?? [config.country];
  const initialCountry = availableCountries.find(country => country === config.country) ?? '';

  const initialValues = {
    shippingAddress: {
      ...(orderState.addresses?.shipping && !orderState.addresses.shipping.collectionPoint ?
        getInitialAddressValues(orderState.addresses.shipping)
      : {
          country: initialCountry,
          gender: customer?.data.gender ?? 'f',
          title: customer?.data.title ?? '',
          firstName: orderState.addresses?.shipping?.firstName ?? customer?.data.firstName ?? '',
          lastName: orderState.addresses?.shipping?.lastName ?? customer?.data.lastName ?? '',
          streetHouseNumber: '',
          street: '',
          houseNumber: '',
          additional: '',
          zipCode: '',
          city: '',
          state: ''
        })
    }
  };

  const onSubmit = async (values: typeof initialValues) => {
    setLoading(LOADING_TYPES.shippingSubmit);
    try {
      const _addressSuggestions = await setShippingAddress(getAddressForSubmission(values.shippingAddress));
      if (_addressSuggestions) {
        setAddressSuggestions(_addressSuggestions);
        return;
      }
      setEditingAddress(false);

      if (isExpressCheckoutReviewPage && config.transactionId) {
        await callApi({
          flow: 'express',
          method: 'PUT',
          endpoint: `/transactions/${config.transactionId}`,
          payload: { providerData: {} }
        });
      }
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'set_collection_point');
    } finally {
      setLoading(LOADING_TYPES.off);
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ submitForm, resetForm, values }) => (
        <>
          {hasShippingAddress && isEditingAddress && (
            <Button
              className="address-overview__btn"
              onClick={() => {
                resetForm();
                setEditingAddress(false);
              }}
              data-test-id="address-overview_cancel-btn"
            >
              <FormattedMessage id="generic.cancel" />
            </Button>
          )}
          <div className="option__form">
            <AddressForm
              headline={<FormattedMessage id="address.shippingAddress" />}
              context="shippingAddress"
              availableCountries={availableCountries}
              isOverview={true}
              addressSuggestions={addressSuggestions}
              setAddressSuggestions={setAddressSuggestions}
            />
            <Button
              type="button"
              className="address-overview__apply-btn"
              onClick={() => submitForm()}
              disabled={!values.shippingAddress.country || !!isLoading}
              isLoading={isLoading === LOADING_TYPES.shippingSubmit}
            >
              <FormattedMessage id="generic.apply" />
            </Button>
          </div>
        </>
      )}
    </Formik>
  );
};

export default AddressOverviewForm;
